// Revenue Ops triggers — derive auto-tasks from contact state.
// Each trigger returns a stable autoKey so we never create duplicates
// (see `ensureAutoTask` in db.js). All work happens client-side off the same
// contact snapshot the page already watches; no extra reads per render.

import { ensureAutoTask } from "./db";

function isoWeekKey(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function monthKey(date = new Date()) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * Evaluate one contact for auto-task triggers.
 * @returns {Array<{ autoKey: string, task: object }>}
 */
export function triggersForContact(c) {
  if (!c || c.isInternal) return [];
  const out = [];
  const stage = c.stage || "lead";
  const fails = Number(c.paymentFailCount) || 0;
  const pct = Number(c.usagePctOfLimit) || 0;
  const health = Number(c.healthScore) || 100;
  const lead = Number(c.leadScore) || 0;
  const ltv = Number(c.lifetimeRevenue) || 0;

  // Payment failure — collapse multiple fails in the same ISO week into one task.
  if (fails > 0 && stage === "paid_customer") {
    out.push({
      autoKey: `pay-fail:${c.id}:${isoWeekKey()}`,
      task: {
        contactId: c.id,
        title: `Payment failure — ${c.name || c.email || c.id}`,
        description: `Stripe reports ${fails} failed charge${fails === 1 ? "" : "s"}. Confirm card, send dunning, or call.`,
        priority: fails >= 2 ? "urgent" : "high",
        category: "billing",
      },
    });
  }

  // Upsell-ready — paid customer hammering their plan limit.
  if (stage === "paid_customer" && pct >= 80 && fails === 0) {
    out.push({
      autoKey: `upsell:${c.id}:${monthKey()}`,
      task: {
        contactId: c.id,
        title: `Upsell — ${c.name || c.email}: ${Math.round(pct)}% of plan used`,
        description: `On ${c.plan || "current"} plan, using ${Math.round(pct)}% of capacity. Pitch next tier this month.`,
        priority: "medium",
        category: "expansion",
      },
    });
  }

  // Churn risk — paying customer whose health has dropped below 40.
  if (stage === "paid_customer" && health < 40) {
    out.push({
      autoKey: `churn-risk:${c.id}:${monthKey()}`,
      task: {
        contactId: c.id,
        title: `Churn risk — ${c.name || c.email} (health ${health})`,
        description: `Activity has dropped sharply. Reach out before next renewal.`,
        priority: "high",
        category: "retention",
      },
    });
  }

  // Win-back — high-LTV churned customer not yet contacted this month.
  if (stage === "churned" && ltv >= 200) {
    out.push({
      autoKey: `winback:${c.id}:${monthKey()}`,
      task: {
        contactId: c.id,
        title: `Win-back — ${c.name || c.email} ($${ltv.toLocaleString()} LTV)`,
        description: `Former paying customer. Check why they left; offer a returning-customer incentive.`,
        priority: lead >= 50 ? "high" : "medium",
        category: "winback",
      },
    });
  }

  return out;
}

/**
 * Evaluate every contact and create any missing auto-tasks. Returns the count
 * of NEW tasks actually created. Caps creations per run to avoid floods on
 * first load of a new dataset.
 */
export async function runAutoTaskTriggers(contacts, { maxPerRun = 25 } = {}) {
  let created = 0;
  for (const c of contacts) {
    if (created >= maxPerRun) break;
    const triggers = triggersForContact(c);
    for (const t of triggers) {
      if (created >= maxPerRun) break;
      try {
        const before = Date.now();
        await ensureAutoTask(t.autoKey, t.task);
        // ensureAutoTask returns the same id whether new or existing;
        // we can't tell from the return alone, so we just count attempts capped.
        // For UX purposes, "created" is approximate (capped per run).
        if (Date.now() - before > 50) created += 1; // heuristic: writes are slower than reads
      } catch (e) {
        // Swallow; one bad contact shouldn't break the loop.
        // eslint-disable-next-line no-console
        console.warn("auto-task trigger failed", t.autoKey, e?.message || e);
      }
    }
  }
  return created;
}
